import React, { useState } from 'react';
import './Pagination.css';
import { colors } from '#styles.variables';
import { TypographySize, TypographyTag, TypographyWeight } from '#ui-kit.types';
import Typography from 'components/ui-kit/Typography';

const Pagination: React.FC<{
  currentPage: number;
  totalRows: number;
  pageSize: number;
  onPageChange: (pageNumber: number, pageSize: number) => void;
}> = ({ currentPage, totalRows, onPageChange, pageSize }) => {
  const [size, setSize] = useState<number>(pageSize);
  const totalPages = Math.ceil(totalRows / pageSize);

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newSize = parseInt(event.target.value);
    setSize(newSize);
    // При изменении размера страницы сбрасываем текущую страницу на первую
    onPageChange(1, newSize);
  };

  const displayPages: any[] = [];

  const getPageNumbers = () => {
    const pagesToShow = 2; // Количество страниц, отображаемых до и после текущей страницы
    const sidePages = pagesToShow * 2; // Общее количество страниц до и после текущей

    if (totalPages <= sidePages) {
      // Если общее количество страниц меньше или равно sidePages, отображаем все страницы
      for (let i = 1; i <= totalPages; i++) {
        displayPages.push(i);
      }
    } else {
      // Иначе отображаем только часть страниц с добавлением многоточия
      const firstPage = Math.max(1, currentPage - pagesToShow);
      const lastPage = Math.min(totalPages, currentPage + pagesToShow);

      // Если первая страница больше 1, добавляем многоточие
      if (firstPage > 1) {
        displayPages.push(1);
        if (firstPage > 2) {
          displayPages.push('...');
        }
      }

      // Отображаем страницы от firstPage до lastPage
      for (let i = firstPage; i <= lastPage; i++) {
        displayPages.push(i);
      }

      // Если последняя страница меньше totalPages, добавляем многоточие
      if (lastPage < totalPages) {
        if (lastPage < totalPages - 1) {
          displayPages.push('...');
        }
        displayPages.push(totalPages);
      }
    }
  };

  getPageNumbers();

  const pageSizes = [pageSize];

  [10, 20, 40].forEach((defaultSize) => {
    if (defaultSize !== pageSize) {
      pageSizes.push(defaultSize);
    }
  });

  return (
    <div className="pagination-container">
      <select
        className="pagination-select"
        value={size}
        onChange={handlePageSizeChange}
      >
        {pageSizes.map((item) => (
          <option key={`page-size-${item}`} value={item}>
            {item}
          </option>
        ))}
      </select>
      <div className="pagination-block">
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage - 1, size)}
          disabled={currentPage === 1}
        >
          Назад
        </button>
        {displayPages.map((pageNumber, index) => {
          if (isNaN(parseInt(pageNumber))) {
            return (
              <Typography
                key={`page-${displayPages}`}
                size={TypographySize.xxl}
                tag={TypographyTag.span}
                weight={TypographyWeight.Light}
                style={{ margin: '0px 10px', color: colors.primary._100 }}
              >
                {pageNumber}
              </Typography>
            );
          }

          return (
            <button
              key={index}
              className={`pagination-button ${
                pageNumber === currentPage ? 'active' : ''
              }`}
              onClick={() => onPageChange(pageNumber, size)}
            >
              {pageNumber}
            </button>
          );
        })}
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage + 1, size)}
          disabled={currentPage === totalPages}
        >
          Далее
        </button>
      </div>
      <div
        style={{
          padding: '8px',
          backgroundColor: '#ffffff',
          border: '1px solid #8fcb24',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          size={TypographySize.m}
          tag={TypographyTag.span}
          weight={TypographyWeight.Light}
          style={{
            marginRight: '10px',
            lineHeight: '100%',
            color: colors.primary._100,
          }}
        >
          Всего:
        </Typography>
        <Typography
          size={TypographySize.m}
          tag={TypographyTag.span}
          weight={TypographyWeight.Bold}
          style={{
            lineHeight: '100%',
            color: colors.primary._100,
          }}
        >
          {totalRows}
        </Typography>
      </div>
    </div>
  );
};

export default Pagination;
